// src/services/apiClient.js
/**
 * @file apiClient.js
 * @description Centralized Axios instance for all backend API calls.
 * Configures base URL, default headers, and interceptors for authentication and error handling.
 */
import axios from "axios";

/**
 * Centralized Axios instance for all backend API calls.
 * - Automatically adds base URL and JSON headers
 * - Adds auth token (if stored)
 * - Can be reused by all services
 */

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "https://api.wiiz.io/v1",
  headers: {
    "Content-Type": "application/json",
  },
});

// 🔒 Add authorization token automatically (if present)
api.interceptors.request.use(
  (config) => {
    const userStr = localStorage.getItem("wiiz_user");
    let token = "";
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        token = user.token || user.access_token || (user.data && user.data.token);
      } catch (e) {
        console.error("Failed to parse user token", e);
      }
    }
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// 🛠️ Unified error logging (optional)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API error:", error.response?.data || error.message);

    // 🔒 Auto-logout on 401 (Invalid/Expired Token)
    if (error.response && error.response.status === 401) {
      const userStr = localStorage.getItem("wiiz_user");
      // Only redirect if we actually had a session that is now invalid
      if (userStr) {
        console.warn("Session expired. Logging out...");
        localStorage.removeItem("wiiz_user");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
